// Módulo comercial (CRM, docs/CONTRATO.md §13; backend/app/api/comercial.py): tipos y funciones de la API.
import { solicitar } from './http';
import type { Comunicacion, Destinatario } from './tiposV2';

export type Etapa = 'prospeccion' | 'calificacion' | 'preventa' | 'propuesta' | 'negociacion' | 'ganada' | 'perdida';
export type TipoCuenta = 'prospecto' | 'cliente' | 'aliado' | 'inactivo';
export type RolDecision = 'decisor' | 'tecnico' | 'compras' | 'usuario' | 'influenciador' | 'campeon' | 'detractor' | 'otro';
export type Moneda = 'COP' | 'USD' | 'EUR' | 'MXN' | 'PAB';
export type NivelRiesgo = 'bajo' | 'medio' | 'alto' | 'critico';
export type TipoActividad = 'llamada' | 'reunion' | 'visita' | 'tarea' | 'nota' | 'correo' | 'seguimiento';
export type CategoriaForecast = 'pipeline' | 'best_case' | 'commit' | 'omitido';
export type AlertaOportunidad = 'sin_proximo_paso' | 'proximo_paso_vencido' | 'cierre_vencido' | 'oferta_sin_seguimiento' | 'estancada' | 'sin_actividad' | 'cierre_desplazado';
export type EstadoActividad = 'pendiente' | 'hecha' | 'cancelada';
export type EstadoProspecto = 'nuevo' | 'contactado' | 'calificado' | 'convertido' | 'descartado';
export type OrigenProspecto = 'referido' | 'web' | 'evento' | 'llamada' | 'correo' | 'redes' | 'licitacion' | 'otro';
export type TipoOportunidad = 'nuevo_negocio' | 'ampliacion' | 'renovacion' | 'servicio';

export interface UsuarioCrm {
  oid: string;
  nombre: string;
  correo: string | null;
  roles: string[];
}

export interface Cuenta {
  id: string;
  nombre: string;
  nit: string | null;
  sector: string | null;
  tipo: TipoCuenta;
  ciudad: string | null;
  pais: string | null;
  direccion: string | null;
  telefono: string | null;
  sitio_web: string | null;
  dominio_correo: string | null;
  tamano: string | null;
  origen: string | null;
  propietario: string | null;
  propietario_nombre?: string | null;
  cuenta_padre: string | null;
  notas: string | null;
  actualizado?: string | null;
  oportunidades_abiertas?: number;
  pipeline?: number;
  ganado?: number;
  casos_preventa?: number;
  contactos?: number;
  ultima_actividad?: string | null;
}

export interface Contacto {
  id: string;
  cuenta_id: string;
  cuenta_nombre?: string;
  nombre: string;
  apellido: string | null;
  cargo: string | null;
  correo: string | null;
  telefono: string | null;
  celular: string | null;
  rol_decision: RolDecision | null;
  principal: boolean;
  notas: string | null;
  activo: boolean;
}

export interface Actividad {
  id: string;
  tipo: TipoActividad;
  asunto: string;
  descripcion: string | null;
  vence: string | null;
  estado: EstadoActividad;
  resultado: string | null;
  propietario: string | null;
  propietario_nombre?: string | null;
  cuenta_id: string | null;
  contacto_id: string | null;
  oportunidad_id: string | null;
  prospecto_id: string | null;
  comunicacion_id: number | null;
  completada: string | null;
  objetivo?: string | null;
  participantes?: string[];
  acuerdos?: string[];
  creado: string;
  oportunidad_codigo?: string | null;
  oportunidad_nombre?: string | null;
  cuenta_nombre?: string | null;
  contacto_nombre?: string | null;
}

export interface Oportunidad {
  id: string;
  codigo: string;
  nombre: string;
  cuenta_id: string;
  cuenta_nombre: string;
  contacto_id: string | null;
  contacto_nombre?: string | null;
  contacto_apellido?: string | null;
  propietario: string | null;
  propietario_nombre?: string | null;
  etapa: Etapa;
  etapa_etiqueta: string;
  probabilidad: number;
  valor: number | null;
  moneda: Moneda;
  valor_cop?: number | null;
  tasa?: number | null;
  tasa_fecha?: string | null;
  tasa_fuente?: string | null;
  renovable?: boolean;
  vigencia_inicio?: string | null;
  vigencia_fin?: string | null;
  renovacion_de?: string | null;
  riesgo_nivel?: NivelRiesgo | null;
  riesgo_puntaje?: number | null;
  riesgo_razones?: { peso: number; texto: string; fuente: string }[];
  riesgo_tendencia?: 'mejorando' | 'estable' | 'deteriorandose' | null;
  riesgo_evaluado?: string | null;
  margen: number | null;
  fecha_cierre: string | null;
  linea: string | null;
  tipo: TipoOportunidad;
  origen: string | null;
  descripcion: string | null;
  competidores: string[];
  proximo_paso: string | null;
  proximo_paso_fecha: string | null;
  motivo_perdida: string | null;
  detalle_cierre: string | null;
  case_id: string | null;
  caso_estado?: string | null;
  offer_id: string | null;
  orden_tablero: number | null;
  proxima_actividad?: string | null;
  abierta: boolean;
  ponderado: number;
  vencida: boolean;
  creado: string;
  actualizado: string;
  cerrada: string | null;
  proximo_paso_responsable: string | null;
  proximo_paso_responsable_nombre?: string | null;
  forecast_categoria: CategoriaForecast;
  forecast_nota: string | null;
  etapa_desde: string;
  ultima_actividad?: string | null;
  cambios_cierre: number;
  dias_abierta: number | null;
  dias_en_etapa: number | null;
  dias_sin_actividad: number | null;
  proximo_paso_vigente: boolean;
  proximo_paso_vencido: boolean;
  alertas: AlertaOportunidad[];
}

export interface HistorialOportunidad {
  id: number;
  campo: string;
  anterior: string | null;
  nuevo: string | null;
  actor: string;
  actor_nombre?: string | null;
  via: string;
  motivo?: string | null;
  cuando: string;
}

export interface CorreoOportunidad {
  id: number;
  tipo: string;
  asunto: string;
  estado: string;
  buzon: string | null;
  destinatarios: Destinatario[];
  fecha_salida: string | null;
  creado: string;
  generado_por?: string | null;
  estado_agente?: EstadoBorradorAgente | null;
}
export type EstadoBorradorAgente = 'pendiente_revision' | 'modificado' | 'aprobado' | 'enviado' | 'descartado' | 'error';

export interface OfertaResumen {
  offer_id: string;
  total_sin_iva: number | null;
  rentabilidad: number | null;
  creado?: string;
}

export interface OportunidadDetalle extends Oportunidad {
  contacto: Contacto | null;
  caso: { case_id: string; tema: string; estado: string; dueno: string | null; fecha_limite: string | null; actualizado: string; ultima_oferta: OfertaResumen | null } | null;
  historial: HistorialOportunidad[];
  correos: CorreoOportunidad[];
  actividades: Actividad[];
  contactos_cuenta: Pick<Contacto, 'id' | 'nombre' | 'apellido' | 'cargo' | 'correo' | 'rol_decision' | 'principal'>[];
  negociacion?: Negociacion | null;
  aprobaciones?: AprobacionComercial[];
  solicitudes_compras?: SolicitudCompras[];
  renovacion?: { id: string; codigo: string; etapa: Etapa } | null;
  renovacion_origen?: { id: string; codigo: string; nombre: string } | null;
}

// ------------------------------------------------------------------ gobierno comercial (§13.9)
export interface Negociacion {
  oportunidad_id: string;
  precio_presentado: number | null;
  precio_objetivo: number | null;
  descuento_solicitado: number | null;
  descuento_motivo: string | null;
  descuento_aprobado: number | null;
  forma_pago: 'contado' | 'credito' | 'hitos' | 'anticipo' | null;
  dias_pago: number | null;
  entrega_solicitada: string | null;
  entrega_comprometida: string | null;
  garantia: 'estandar' | 'especial' | null;
  garantia_detalle: string | null;
  cambios_alcance: string | null;
  objecion_principal: 'precio' | 'plazo' | 'alcance' | 'competencia' | 'condiciones' | 'otra' | null;
  competidor_principal: string | null;
  actualizado?: string | null;
}
export interface NegociacionVista extends Negociacion {
  condiciones_sin_aprobar: { tipo: TipoAprobacion; detalle: string; valor: number | null }[];
  politica: { banda_descuento: number; margen_minimo: number; dias_credito_estandar: number };
}
export type TipoAprobacion = 'descuento' | 'credito' | 'margen' | 'condicion' | 'entrega' | 'garantia';
export interface AprobacionComercial {
  id: number;
  oportunidad_id: string;
  tipo: TipoAprobacion;
  etiqueta?: string;
  detalle: string;
  valor: number | null;
  rol_aprobador: string;
  estado: 'pendiente' | 'aprobada' | 'rechazada' | 'cancelada';
  origen: string;
  solicitada_por: string;
  solicitada_por_nombre?: string | null;
  decidida_por: string | null;
  decidida_por_nombre?: string | null;
  comentario: string | null;
  creado: string;
  decidida: string | null;
  oportunidad_codigo?: string;
  oportunidad_nombre?: string;
  cuenta_nombre?: string;
  puede_decidir?: boolean;
}
export type TipoCompras = 'disponibilidad' | 'precio' | 'proveedor' | 'lead_time';
export interface SolicitudCompras {
  id: number;
  oportunidad_id: string;
  tipo: TipoCompras;
  detalle: string;
  fecha_requerida: string | null;
  estado: 'abierta' | 'en_gestion' | 'respondida' | 'cancelada';
  respuesta: string | null;
  respondida_por: string | null;
  respondida_por_nombre?: string | null;
  origen: string;
  solicitada_por: string;
  solicitada_por_nombre?: string | null;
  creado: string;
  respondida: string | null;
  oportunidad_codigo?: string;
  oportunidad_nombre?: string;
  cuenta_nombre?: string;
}

export interface EventoCuenta {
  tipo: string;
  fecha: string;
  titulo: string;
  detalle?: string | null;
  actor?: string | null;
  estado?: string;
  oportunidad?: string | null;
  case_id?: string | null;
  via?: string;
}

/** Vista 360 de la cuenta. En la lista `contactos` es un conteo; aquí es la lista. */
export interface CuentaVista extends Omit<Cuenta, 'contactos'> {
  contactos: Contacto[];
  padre: { id: string; nombre: string } | null;
  filiales: { id: string; nombre: string; tipo: TipoCuenta; ciudad: string | null }[];
  oportunidades: Oportunidad[];
  casos: { case_id: string; tema: string; estado: string; tipo: string; valor_estimado: number | null; creado: string; ultima_oferta: OfertaResumen | null }[];
  actividades: Actividad[];
  linea_tiempo: EventoCuenta[];
  totales: { pipeline: number; ponderado: number; ganado: number; perdido: number; oportunidades_abiertas: number };
}

export interface Prospecto {
  id: string;
  codigo: string;
  nombre: string;
  empresa: string;
  cargo: string | null;
  correo: string | null;
  telefono: string | null;
  ciudad: string | null;
  origen: OrigenProspecto;
  necesidad: string | null;
  estado: EstadoProspecto;
  motivo_descarte: string | null;
  propietario: string | null;
  cuenta_id: string | null;
  oportunidad_id: string | null;
  creado: string;
  actualizado: string;
}

export interface FilaEmbudo {
  etapa: Etapa;
  etiqueta: string;
  n: number;
  valor: number;
  ponderado: number;
}

export interface CifrasPeriodo {
  ganado: number;
  commit: number;
  best_case: number;
  pipeline: number;
  ponderado: number;
  n_commit: number;
  n_pipeline: number;
  meta: number | null;
  gap: number | null;
  avance: number | null;
  cobertura: number | null;
}

export interface AlertaResumen {
  clave: AlertaOportunidad | 'desplazado_30d';
  n: number;
  valor: number;
  ids: string[];
}

export interface Forecast {
  desde: string;
  periodos: (CifrasPeriodo & { periodo: string })[];
  responsables: (CifrasPeriodo & { propietario: string; nombre: string })[];
  oportunidades_mes: Oportunidad[];
  vencidas: { n: number; valor: number };
  sin_fecha: { n: number; valor: number };
  desplazado_30d: { n: number; valor: number; ids: string[] };
}

export interface Meta {
  id: number;
  periodo: string;
  propietario: string | null;
  propietario_nombre?: string | null;
  linea: string | null;
  valor: number;
}

// ------------------------------------------------------------------ comunicaciones (§13.8)
export type EstadoCorreo = 'enviado' | 'sin_respuesta' | 'requiere_seguimiento' | 'respondido' | 'cerrado' | 'por_responder' | 'recibido';
export type TipoSugerencia = 'actualizar_fecha_cierre' | 'actualizar_valor' | 'crear_tarea' | 'notificar_preventa' | 'solicitar_aprobacion' | 'registrar_competidor' | 'crear_seguimiento';
export type TipoNota = 'informacion' | 'solicitud' | 'riesgo' | 'bloqueo' | 'decision' | 'cambio_alcance';

export interface Hallazgo {
  tipo: string;
  texto: string;
  evidencia: string;
}
export interface Sugerencia {
  id: number;
  oportunidad_id: string;
  correo_id: number | null;
  actividad_id: string | null;
  tipo: TipoSugerencia;
  texto: string;
  evidencia: string | null;
  datos: { fecha?: string; valor?: number; competidor?: string };
  estado: 'pendiente' | 'aplicada' | 'descartada';
  creado: string;
}
export interface AdjuntoCorreo {
  id: string;
  nombre: string;
  content_type: string | null;
  bytes: number | null;
  vinculado: boolean;
}
export interface Candidato {
  oportunidad_id: string;
  codigo: string;
  nombre: string;
  cuenta_id: string;
  motivo: string;
}
export interface CorreoComercial {
  id: number;
  buzon: string;
  internet_message_id: string;
  conversation_id: string | null;
  direccion: 'entrada' | 'salida';
  remitente: string | null;
  remitente_nombre: string | null;
  para: string[];
  cc: string[];
  asunto: string;
  cuerpo: string;
  fecha: string;
  es_reenvio: boolean;
  cuenta_id: string | null;
  cuenta_nombre?: string | null;
  contacto_id: string | null;
  oportunidad_id: string | null;
  vinculo: 'automatico' | 'manual' | 'pendiente' | 'descartado';
  vinculo_motivo: string | null;
  candidatos: Candidato[];
  cerrado: boolean;
  analisis: { resumen: string; requiere_respuesta: boolean; fecha_objetivo: string | null; hallazgos: Hallazgo[] } | null;
  error_analisis: string | null;
  estado: EstadoCorreo;
  dias: number;
  adjuntos: AdjuntoCorreo[];
  sugerencias: Sugerencia[];
  interno_remitente: boolean;
  comunicacion_id?: number | null;
}
export interface NotaInterna {
  id: number;
  oportunidad_id: string;
  tipo: TipoNota;
  texto: string;
  autor: string;
  autor_nombre?: string | null;
  notificado_preventa: boolean;
  creado: string;
}
export interface Compromiso {
  id: number;
  oportunidad_id: string;
  descripcion: string;
  lado: 'cliente' | 'upk';
  responsable: string | null;
  fecha: string | null;
  estado: 'pendiente' | 'cumplido' | 'cancelado';
  origen: string;
  correo_id: number | null;
  actividad_id: string | null;
  evidencia: string | null;
  creado: string;
}
export interface ItemCronologia {
  clave: string;
  tipo: string;
  fecha: string;
  interno: boolean;
  titulo: string;
  autor?: string | null;
  texto?: string | null;
  case_id?: string;
  accion?: string;
  correo?: CorreoComercial;
  actividad?: Actividad & { sugerencias: Sugerencia[] };
  nota?: NotaInterna;
  compromiso?: Compromiso;
  cambio?: HistorialOportunidad;
  aprobacion?: { offer_id: string; decision: string; comentario?: string | null; aprobador_nombre?: string | null };
}
export interface ComunicacionesOportunidad {
  items: ItemCronologia[];
  pendientes_vincular: number;
  sugerencias_pendientes: Sugerencia[];
  compromisos_pendientes: Compromiso[];
}
export interface Conversacion {
  asunto?: string | null;
  fecha?: string | null;
  contacto_id?: string | null;
  participantes?: string[];
  resumen: string;
  resultado?: string | null;
  acuerdos?: string[];
  compromisos?: { descripcion: string; lado: 'cliente' | 'upk'; fecha?: string | null; responsable?: string | null }[];
  proximo_paso?: string | null;
  proximo_paso_fecha?: string | null;
  proximo_paso_responsable?: string | null;
  cambio_alcance?: boolean;
  detalle_alcance?: string | null;
}

export interface ResumenComercial {
  alertas: AlertaResumen[];
  atencion: Oportunidad[];
  mes: CifrasPeriodo & { periodo: string };
  embudo: FilaEmbudo[];
  pipeline: number;
  ponderado: number;
  ganado_mes: number;
  ganado_anio: number;
  tasa_cierre: number | null;
  ciclo_dias: number | null;
  actividades_hoy: Actividad[];
  actividades_proximas: Actividad[];
  por_cerrar: Oportunidad[];
  sin_proximo_paso: Oportunidad[];
}

export type NuevaCuenta = Partial<Omit<Cuenta, 'id'>> & { nombre: string };
export type NuevoContacto = Partial<Omit<Contacto, 'id' | 'cuenta_id'>> & { nombre: string };
export type NuevaOportunidad = Partial<Pick<Oportunidad, 'contacto_id' | 'propietario' | 'etapa' | 'valor' | 'moneda' | 'tasa' | 'tasa_fecha' | 'tasa_fuente' | 'fecha_cierre' | 'linea' | 'tipo' | 'origen' | 'descripcion' | 'competidores' | 'proximo_paso' | 'proximo_paso_fecha' | 'proximo_paso_responsable'>> & {
  nombre: string;
  cuenta_id: string;
};
export type CambiosOportunidad = Partial<Pick<Oportunidad, 'nombre' | 'contacto_id' | 'propietario' | 'probabilidad' | 'valor' | 'moneda' | 'fecha_cierre' | 'linea' | 'tipo' | 'origen' | 'descripcion' | 'competidores' | 'proximo_paso' | 'proximo_paso_fecha' | 'proximo_paso_responsable' | 'motivo_perdida' | 'detalle_cierre' | 'forecast_categoria' | 'forecast_nota' | 'tasa' | 'tasa_fecha' | 'tasa_fuente' | 'renovable' | 'vigencia_inicio' | 'vigencia_fin'>>;
export interface Movimiento {
  orden?: number | null;
  motivo_perdida?: string | null;
  detalle_cierre?: string | null;
  valor?: number | null;
  proximo_paso?: string | null;
  proximo_paso_fecha?: string | null;
  proximo_paso_responsable?: string | null;
  excepcion?: string | null;
}
export type NuevaActividad = Partial<Pick<Actividad, 'descripcion' | 'vence' | 'estado' | 'resultado' | 'propietario' | 'cuenta_id' | 'contacto_id' | 'oportunidad_id' | 'prospecto_id' | 'objetivo'>> & { tipo: TipoActividad; asunto: string };
export type NuevoProspecto = Partial<Omit<Prospecto, 'id' | 'codigo' | 'creado' | 'actualizado' | 'cuenta_id' | 'oportunidad_id'>> & { nombre: string; empresa: string };

const R = '/api/comercial';
const items = async <T,>(p: Promise<{ items: T[] }>) => (await p).items;
const limpio = (q: Record<string, string | boolean | null | undefined>) =>
  Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => [k, String(v)]));

export const crm = {
  resumen: (mias = false) => solicitar<ResumenComercial>({ metodo: 'GET', ruta: `${R}/resumen`, query: mias ? { mias: 'true' } : undefined }),
  usuarios: () => items(solicitar<{ items: UsuarioCrm[] }>({ metodo: 'GET', ruta: `${R}/usuarios` })),
  forecast: (f: { desde?: string; meses?: number; propietario?: string; linea?: string } = {}) =>
    solicitar<Forecast>({ metodo: 'GET', ruta: `${R}/forecast`, query: limpio({ desde: f.desde, meses: f.meses ? String(f.meses) : undefined, propietario: f.propietario, linea: f.linea }) }),
  metas: (desde?: string, hasta?: string) => items(solicitar<{ items: Meta[] }>({ metodo: 'GET', ruta: `${R}/metas`, query: limpio({ desde, hasta }) })),
  guardarMeta: (d: { periodo: string; propietario?: string | null; linea?: string | null; valor: number }) => solicitar<Meta>({ metodo: 'PUT', ruta: `${R}/metas`, cuerpo: d }),
  eliminarMeta: (id: number) => solicitar<void>({ metodo: 'DELETE', ruta: `${R}/metas/${id}` }),

  cuentas: (f: { q?: string; tipo?: string; propietario?: string } = {}) => items(solicitar<{ items: Cuenta[] }>({ metodo: 'GET', ruta: `${R}/cuentas`, query: limpio(f) })),
  cuenta: (id: string) => solicitar<CuentaVista>({ metodo: 'GET', ruta: `${R}/cuentas/${id}` }),
  crearCuenta: (d: NuevaCuenta) => solicitar<CuentaVista>({ metodo: 'POST', ruta: `${R}/cuentas`, cuerpo: d }),
  actualizarCuenta: (id: string, d: Partial<NuevaCuenta>) => solicitar<CuentaVista>({ metodo: 'PATCH', ruta: `${R}/cuentas/${id}`, cuerpo: d }),

  contactos: (q?: string) => items(solicitar<{ items: Contacto[] }>({ metodo: 'GET', ruta: `${R}/contactos`, query: limpio({ q }) })),
  crearContacto: (cuentaId: string, d: NuevoContacto) => solicitar<Contacto>({ metodo: 'POST', ruta: `${R}/cuentas/${cuentaId}/contactos`, cuerpo: d }),
  actualizarContacto: (id: string, d: Partial<NuevoContacto>) => solicitar<Contacto>({ metodo: 'PATCH', ruta: `${R}/contactos/${id}`, cuerpo: d }),

  prospectos: (estado?: string) => items(solicitar<{ items: Prospecto[] }>({ metodo: 'GET', ruta: `${R}/prospectos`, query: limpio({ estado }) })),
  crearProspecto: (d: NuevoProspecto) => solicitar<Prospecto>({ metodo: 'POST', ruta: `${R}/prospectos`, cuerpo: d }),
  actualizarProspecto: (id: string, d: Partial<NuevoProspecto>) => solicitar<Prospecto>({ metodo: 'PATCH', ruta: `${R}/prospectos/${id}`, cuerpo: d }),
  convertirProspecto: (id: string, d: { cuenta_id?: string | null; crear_oportunidad?: boolean; nombre_oportunidad?: string | null; valor?: number | null; fecha_cierre?: string | null; proximo_paso?: string | null; proximo_paso_fecha?: string | null }) =>
    solicitar<{ cuenta: Cuenta; contacto: Contacto; oportunidad: Oportunidad | null }>({ metodo: 'POST', ruta: `${R}/prospectos/${id}/convertir`, cuerpo: d }),

  oportunidades: (f: { etapa?: string; abiertas?: boolean; propietario?: string; cuenta_id?: string; q?: string } = {}) =>
    items(solicitar<{ items: Oportunidad[] }>({ metodo: 'GET', ruta: `${R}/oportunidades`, query: limpio(f) })),
  oportunidad: (id: string) => solicitar<OportunidadDetalle>({ metodo: 'GET', ruta: `${R}/oportunidades/${id}` }),
  crearOportunidad: (d: NuevaOportunidad) => solicitar<OportunidadDetalle>({ metodo: 'POST', ruta: `${R}/oportunidades`, cuerpo: d }),
  actualizarOportunidad: (id: string, d: CambiosOportunidad) => solicitar<OportunidadDetalle>({ metodo: 'PATCH', ruta: `${R}/oportunidades/${id}`, cuerpo: d }),
  mover: (id: string, etapa: Etapa, extra: Movimiento = {}) =>
    solicitar<OportunidadDetalle>({ metodo: 'POST', ruta: `${R}/oportunidades/${id}/mover`, cuerpo: { etapa, ...extra } }),
  solicitarPreventa: (id: string, d: { tema?: string; tipo?: string; fecha_limite?: string | null; descripcion?: string }) =>
    solicitar<{ case_id: string; nuevo: boolean; oportunidad: OportunidadDetalle }>({ metodo: 'POST', ruta: `${R}/oportunidades/${id}/preventa`, cuerpo: d }),
  borradorCorreo: (id: string, d: { destinatarios: Destinatario[]; cc?: Destinatario[]; asunto: string; contenido: string }) =>
    solicitar<Comunicacion>({ metodo: 'POST', ruta: `${R}/oportunidades/${id}/correos`, cuerpo: d }),

  comunicaciones: (id: string, vista: 'comunicaciones' | 'completa' = 'comunicaciones') =>
    solicitar<ComunicacionesOportunidad>({ metodo: 'GET', ruta: `${R}/oportunidades/${id}/comunicaciones`, query: { vista } }),
  correosPendientes: (todos = false) => items(solicitar<{ items: CorreoComercial[] }>({ metodo: 'GET', ruta: `${R}/correos/pendientes`, query: todos ? { todos: 'true' } : undefined })),
  vincularCorreo: (id: number, d: { oportunidad_id?: string; cuenta_id?: string; descartar?: boolean }) => solicitar<CorreoComercial>({ metodo: 'POST', ruta: `${R}/correos/${id}/vincular`, cuerpo: d }),
  responderCorreo: (id: number, d: { contenido: string; todos?: boolean; reenviar?: boolean; destinatarios?: Destinatario[] }) =>
    solicitar<Comunicacion>({ metodo: 'POST', ruta: `${R}/correos/${id}/responder`, cuerpo: d }),
  seguimientoCorreo: (id: number) => solicitar<Comunicacion>({ metodo: 'POST', ruta: `${R}/correos/${id}/seguimiento`, cuerpo: {} }),
  cerrarCorreo: (id: number, cerrado = true) => solicitar<CorreoComercial>({ metodo: 'POST', ruta: `${R}/correos/${id}/cerrar`, query: { cerrado: String(cerrado) }, cuerpo: {} }),
  analizarCorreo: (id: number) => solicitar<{ ok: boolean }>({ metodo: 'POST', ruta: `${R}/correos/${id}/analizar`, cuerpo: {} }),
  crearNota: (id: string, d: { tipo: TipoNota; texto: string; responsable?: string | null }) => solicitar<NotaInterna>({ metodo: 'POST', ruta: `${R}/oportunidades/${id}/notas`, cuerpo: d }),
  crearCompromiso: (id: string, d: { descripcion: string; lado: 'cliente' | 'upk'; fecha?: string | null; correo_id?: number | null; evidencia?: string | null }) =>
    solicitar<Compromiso>({ metodo: 'POST', ruta: `${R}/oportunidades/${id}/compromisos`, cuerpo: d }),
  cambiarCompromiso: (id: number, estado: Compromiso['estado']) => solicitar<Compromiso>({ metodo: 'PATCH', ruta: `${R}/compromisos/${id}`, cuerpo: { estado } }),
  registrarConversacion: (id: string, tipo: 'llamadas' | 'reuniones', d: Conversacion) => solicitar<Actividad>({ metodo: 'POST', ruta: `${R}/oportunidades/${id}/${tipo}`, cuerpo: d }),
  aplicarSugerencia: (id: number, d: { fecha?: string | null; valor?: number | null; competidor?: string | null; texto?: string | null } = {}) =>
    solicitar<Sugerencia>({ metodo: 'POST', ruta: `${R}/sugerencias/${id}/aplicar`, cuerpo: d }),
  descartarSugerencia: (id: number) => solicitar<Sugerencia>({ metodo: 'POST', ruta: `${R}/sugerencias/${id}/descartar`, cuerpo: {} }),
  vincularAdjunto: (id: string) => solicitar<AdjuntoCorreo>({ metodo: 'POST', ruta: `${R}/adjuntos/${id}/vincular`, cuerpo: {} }),
  descargarAdjunto: (id: string) => solicitar<Blob>({ metodo: 'GET', ruta: `${R}/adjuntos/${id}`, binario: true }),

  actividades: (f: { mias?: boolean; pendientes?: boolean } = {}) =>
    items(solicitar<{ items: Actividad[] }>({ metodo: 'GET', ruta: `${R}/actividades`, query: limpio({ mias: f.mias ?? true, pendientes: f.pendientes ?? true }) })),
  crearActividad: (d: NuevaActividad) => solicitar<Actividad>({ metodo: 'POST', ruta: `${R}/actividades`, cuerpo: d }),
  actualizarActividad: (id: string, d: Partial<Pick<Actividad, 'asunto' | 'descripcion' | 'vence' | 'estado' | 'resultado' | 'propietario' | 'objetivo'>>) =>
    solicitar<Actividad>({ metodo: 'PATCH', ruta: `${R}/actividades/${id}`, cuerpo: d }),
};

// ------------------------------------------------------------------ etiquetas
export const ETAPAS: Etapa[] = ['prospeccion', 'calificacion', 'preventa', 'propuesta', 'negociacion', 'ganada', 'perdida'];
export const ETAPAS_ABIERTAS: Etapa[] = ['prospeccion', 'calificacion', 'preventa', 'propuesta', 'negociacion'];
export const ETIQUETA_ETAPA: Record<Etapa, string> = {
  prospeccion: 'Prospección',
  calificacion: 'Calificación',
  preventa: 'Diseño en preventa',
  propuesta: 'Propuesta enviada',
  negociacion: 'Negociación',
  ganada: 'Ganada',
  perdida: 'Perdida',
};
export const PROBABILIDAD_ETAPA: Record<Etapa, number> = { prospeccion: 10, calificacion: 20, preventa: 40, propuesta: 60, negociacion: 80, ganada: 100, perdida: 0 };
export const ETIQUETA_TIPO_CUENTA: Record<TipoCuenta, string> = { prospecto: 'Prospecto', cliente: 'Cliente', aliado: 'Aliado', inactivo: 'Inactiva' };
export const ETIQUETA_ROL_DECISION: Record<RolDecision, string> = {
  decisor: 'Decisor económico',
  tecnico: 'Decisor técnico',
  compras: 'Compras',
  usuario: 'Usuario',
  influenciador: 'Influenciador',
  campeon: 'Campeón interno',
  detractor: 'Detractor',
  otro: 'Otro',
};
export const MONEDAS: Moneda[] = ['COP', 'USD', 'EUR', 'MXN', 'PAB'];
export const ETIQUETA_RIESGO: Record<NivelRiesgo, string> = { bajo: 'Riesgo bajo', medio: 'Riesgo medio', alto: 'Riesgo alto', critico: 'Riesgo crítico' };
export const ETIQUETA_APROBACION: Record<TipoAprobacion, string> = {
  descuento: 'Descuento',
  credito: 'Crédito o plazo de pago',
  margen: 'Margen bajo el mínimo',
  condicion: 'Condición contractual',
  entrega: 'Entrega excepcional',
  garantia: 'Garantía no estándar',
};
export const ETIQUETA_COMPRAS: Record<TipoCompras, string> = { disponibilidad: 'Disponibilidad', precio: 'Precio', proveedor: 'Proveedor', lead_time: 'Lead time' };
export const ETIQUETA_BORRADOR_AGENTE: Record<EstadoBorradorAgente, string> = {
  pendiente_revision: 'Pendiente de revisión',
  modificado: 'Modificado por usted',
  aprobado: 'Aprobado',
  enviado: 'Enviado',
  descartado: 'Descartado',
  error: 'Error',
};
export const ETIQUETA_ACTIVIDAD: Record<TipoActividad, string> = { llamada: 'Llamada', reunion: 'Reunión', visita: 'Visita', tarea: 'Tarea', nota: 'Nota', correo: 'Correo', seguimiento: 'Seguimiento' };
/** Actividades que al completarse exigen el resultado (COM-26). */
export const ALTO_IMPACTO: TipoActividad[] = ['reunion', 'visita', 'seguimiento'];
export const ETIQUETA_FORECAST: Record<CategoriaForecast, string> = { pipeline: 'Pipeline', best_case: 'Best Case', commit: 'Commit', omitido: 'Omitido' };
export const AYUDA_FORECAST: Record<CategoriaForecast, string> = {
  pipeline: 'Abierta, todavía no probable para el mes de cierre.',
  best_case: 'Puede cerrar en el mes, con dependencias relevantes.',
  commit: 'El responsable se compromete a cerrarla en el mes, con sustento.',
  omitido: 'Abierta, pero no cuenta en el forecast por ahora.',
};
export const ETIQUETA_ALERTA: Record<AlertaOportunidad | 'desplazado_30d', string> = {
  sin_proximo_paso: 'Sin próximo paso',
  proximo_paso_vencido: 'Próximo paso vencido',
  cierre_vencido: 'Fecha de cierre vencida',
  oferta_sin_seguimiento: 'Oferta sin seguimiento',
  estancada: 'Estancada en la etapa',
  sin_actividad: 'Sin actividad reciente',
  cierre_desplazado: 'Cierre desplazado varias veces',
  desplazado_30d: 'Cierre movido a un mes posterior (30 días)',
};
export const ETIQUETA_ORIGEN: Record<OrigenProspecto, string> = {
  referido: 'Referido',
  web: 'Sitio web',
  evento: 'Evento',
  llamada: 'Llamada',
  correo: 'Correo',
  redes: 'Redes sociales',
  licitacion: 'Licitación',
  otro: 'Otro',
};
export const ETIQUETA_ESTADO_PROSPECTO: Record<EstadoProspecto, string> = {
  nuevo: 'Nuevo',
  contactado: 'Contactado',
  calificado: 'Calificado',
  convertido: 'Convertido',
  descartado: 'Descartado',
};
export const ETIQUETA_TIPO_OPORTUNIDAD: Record<TipoOportunidad, string> = {
  nuevo_negocio: 'Negocio nuevo',
  ampliacion: 'Ampliación',
  renovacion: 'Renovación',
  servicio: 'Servicio',
};
export const MOTIVOS_PERDIDA = ['Precio', 'Competencia', 'Alcance técnico', 'Tiempo de entrega', 'Sin presupuesto', 'El cliente desistió', 'Sin respuesta del cliente', 'Otro'];
export const LINEAS = ['CCTV y videovigilancia', 'Control de acceso', 'Cableado estructurado', 'Redes y wifi', 'Energía y UPS', 'Detección de incendio', 'Automatización', 'Servicios y mantenimiento', 'Otra'];

export const ETIQUETA_ESTADO_CORREO: Record<EstadoCorreo, string> = {
  enviado: 'Enviado',
  sin_respuesta: 'Sin respuesta',
  requiere_seguimiento: 'Requiere seguimiento',
  respondido: 'Respondido',
  cerrado: 'Cerrado',
  por_responder: 'Por responder',
  recibido: 'Recibido',
};
export const ETIQUETA_SUGERENCIA: Record<TipoSugerencia, string> = {
  actualizar_fecha_cierre: 'Actualizar fecha de cierre',
  actualizar_valor: 'Actualizar valor',
  crear_tarea: 'Crear tarea',
  notificar_preventa: 'Notificar a Preventa',
  solicitar_aprobacion: 'Solicitar aprobación',
  registrar_competidor: 'Registrar competidor',
  crear_seguimiento: 'Crear seguimiento',
};
export const ETIQUETA_NOTA: Record<TipoNota, string> = {
  informacion: 'Información',
  solicitud: 'Solicitud (crea tarea)',
  riesgo: 'Riesgo',
  bloqueo: 'Bloqueo',
  decision: 'Decisión',
  cambio_alcance: 'Cambio de alcance (avisa a Preventa)',
};
export const ETIQUETA_HALLAZGO: Record<string, string> = {
  descuento: 'Descuento',
  decision: 'Decisión',
  cambio_cantidad: 'Cambio de cantidad',
  competidor: 'Competidor',
  credito: 'Crédito',
  plazo_entrega: 'Plazo de entrega',
  alcance: 'Alcance',
  presupuesto: 'Presupuesto',
  precio: 'Precio',
  aprobacion: 'Aprobación del cliente',
  riesgo: 'Riesgo',
  otro: 'Otro',
};
